import React, { useState, useEffect, useMemo } from 'react';
import { unitLabels } from '../utils/units';
import {
  Box,
  Card,
  CardContent,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Grid,
  FormControl,
  Select,
  MenuItem,
  Chip,
  useTheme,
  alpha,
  Paper,
  Tooltip as MuiTooltip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  ComposedChart,
  ReferenceLine,
  ZAxis
} from 'recharts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { format, subDays, addDays, eachDayOfInterval, parseISO } from 'date-fns';
// Removed unused imports for consistent data source usage
import { CostComplianceConfig } from './CostOfNonComplianceSettings';
import CalInsightButton from './CalInsightButton';

interface NonComplianceItem {
  meter_product_id: string;
  meter_id: string;
  product_type: string;
  display_name: string;
  current_meter_factor: number;
  drift_percent: number;
  days_out_of_compliance: number;
  average_annual_flow: number;
  affected_volume: number;
  product_price: number;
  financial_impact: number;
  annual_impact: number;
  daily_impact?: number;
  impact_type: 'Loss' | 'Gain' | 'In Compliance';
  meter_direction: string;
  compliance_status?: 'in_compliance' | 'loss' | 'gain';
  last_conforming_date: string | null;
  historical_loss?: number;  // Actual historical loss calculated by backend
  historical_gain?: number;  // Actual historical gain calculated by backend (displayed as exposure)
}

interface ChartProps {
  items: NonComplianceItem[];
  summary: any;
  config?: CostComplianceConfig;
  timeRange?: number;
  onTimeRangeChange?: (timeRange: number) => void;
  dailyTrend?: Array<{
    date: string;
    loss: number;
    gain: number;
    net: number;
  }>;
  onMeterClick?: (meterProductId: string) => void;
}

// Dynamic color palette for products using Trindent brand colors
const getProductColor = (productName: string): string => {
  // Generate a consistent color based on the product name hash using Trindent palette
  const colors = [
    '#0069AA', // Trindent Blue
    '#2F5496', // Dark Blue
    '#8EAADB', // Medium Blue
    '#B4C6E7', // Light Blue
    '#002060', // Dark Navy
    '#D9E2F3', // Lightest Blue
    '#005488', // Darker Trindent Blue
    '#003F66', // Even Darker Blue
    '#66A5CF', // Light-Medium Blue
    '#99C3DF', // Lighter Blue
    '#CCE1EF', // Very Light Blue
    '#3387BF'  // Medium-Dark Blue
  ];

  // Simple hash function to get consistent index
  let hash = 0;
  for (let i = 0; i < productName.length; i++) {
    hash = ((hash << 5) - hash) + productName.charCodeAt(i);
    hash = hash & hash; // Convert to 32bit integer
  }

  return colors[Math.abs(hash) % colors.length];
};

const CostOfNonComplianceCharts: React.FC<ChartProps> = ({
  items,
  summary,
  config,
  timeRange: externalTimeRange,
  onTimeRangeChange,
  dailyTrend,
  onMeterClick
}) => {
  const theme = useTheme();
  const [internalTimeRange, setInternalTimeRange] = useState<30 | 90 | 365 | 1095 | 1825>(30);
  const timeRange = externalTimeRange || internalTimeRange;
  const setTimeRange = onTimeRangeChange || setInternalTimeRange;
  const [selectedProduct, setSelectedProduct] = useState<string>('ALL');
  const [pieDisplayMode, setPieDisplayMode] = useState<'percentage' | 'value'>('percentage');
  const [hoveredMeterProduct, setHoveredMeterProduct] = useState<string | null>(null);

  // Debug: Log what data we're receiving
  // Charts received items and summary

  // Removed historical data fetching - all charts now use consistent items data

  // Get unique products
  const products = useMemo(() => {
    const productSet = new Set(items.map(item => item.product_type));
    return ['ALL', ...Array.from(productSet).sort()];
  }, [items]);

  // Filter items by selected product
  const filteredItems = useMemo(() => {
    if (selectedProduct === 'ALL') return items;
    return items.filter(item => item.product_type === selectedProduct);
  }, [items, selectedProduct]);

  // 1. Daily Loss Trend Data - Filter by selected product
  const dailyTrendData = useMemo(() => {
    // If we have real daily trend data from the backend, use it
    if (dailyTrend && dailyTrend.length > 0) {
      // Determine appropriate date format based on time range
      const dateFormat = timeRange <= 90 ? 'MMM dd' :
                         timeRange <= 365 ? 'MMM' :
                         'MMM yyyy';

      // When a product is selected, we need to recalculate the daily totals
      // based only on the filtered items
      if (selectedProduct !== 'ALL') {
        // Group filtered items by date and calculate daily totals
        const dailyTotals: Record<string, { loss: number; gain: number }> = {};

        filteredItems.forEach(item => {
          // Use the item's daily impact to build daily totals
          // This is a simplified calculation - ideally backend would provide product-filtered data
          const dailyImpact = Math.abs(item.daily_impact || 0);

          // For each day in the period, add this meter's contribution
          dailyTrend.forEach(day => {
            const dateKey = day.date;
            if (!dailyTotals[dateKey]) {
              dailyTotals[dateKey] = { loss: 0, gain: 0 };
            }

            if (item.impact_type === 'Loss') {
              dailyTotals[dateKey].loss += dailyImpact;
            } else if (item.impact_type === 'Gain') {
              dailyTotals[dateKey].gain += dailyImpact;
            }
          });
        });

        return dailyTrend.map(day => ({
          date: format(parseISO(day.date), dateFormat),
          fullDate: day.date,
          losses: dailyTotals[day.date]?.loss || 0,
          exposures: dailyTotals[day.date]?.gain || 0,
          net: (dailyTotals[day.date]?.gain || 0) - (dailyTotals[day.date]?.loss || 0),
          metersAffected: filteredItems.length
        }));
      }

      // No product filter - use original data
      return dailyTrend.map(day => ({
        date: format(parseISO(day.date), dateFormat),
        fullDate: day.date,
        losses: day.loss || 0,  // Use real loss data
        exposures: day.gain || 0,   // Use real exposure data (backend still sends as 'gain')
        net: day.net || 0,
        metersAffected: filteredItems.length
      }));
    }

    // Fallback: If no backend data, calculate from filtered items
    const endDate = new Date();
    const startDate = subDays(endDate, timeRange);
    const days = eachDayOfInterval({ start: startDate, end: endDate });

    // Determine appropriate date format based on time range
    const dateFormat = timeRange <= 90 ? 'MMM dd' :
                       timeRange <= 365 ? 'MMM' :
                       'MMM yyyy';

    // Calculate daily totals from filtered items
    const dailyData = days.map(date => {
      let dayLoss = 0;
      let dayGain = 0;

      // Sum up the daily impact from filtered items
      filteredItems.forEach(item => {
        const dailyImpact = Math.abs(item.daily_impact || 0);
        if (item.impact_type === 'Loss') {
          dayLoss += dailyImpact;
        } else if (item.impact_type === 'Gain') {
          dayGain += dailyImpact;
        }
      });

      return {
        date: format(date, dateFormat),
        fullDate: date.toISOString(),
        losses: dayLoss,
        exposures: dayGain,
        net: dayGain - dayLoss,
        metersAffected: filteredItems.length
      };
    });

    return dailyData;
  }, [timeRange, filteredItems, dailyTrend, selectedProduct]);  // Added selectedProduct dependency

  // 2. Product Distribution Data - Based on actual historical losses from backend
  const productDistributionData = useMemo(() => {
    const productTotals: Record<string, number> = {};

    // Use the historical_loss_in_period field from backend if available
    // This contains ACTUAL losses calculated from when meters went in/out of compliance
    filteredItems.forEach(item => {
      if (!productTotals[item.product_type]) {
        productTotals[item.product_type] = 0;
      }

      // Use historical_loss if available (backend calculated actual losses)
      // Otherwise fall back to calculated estimate
      if (item.historical_loss !== undefined) {
        // Backend has already calculated actual historical loss for this meter
        productTotals[item.product_type] += Math.abs(item.historical_loss || 0);
      } else if (item.impact_type === 'Loss') {
        // Fallback: estimate based on days out and daily impact
        const daysToCount = Math.min(item.days_out_of_compliance || 0, timeRange);
        const dailyLoss = Math.abs(item.daily_impact || 0);
        const historicalLoss = dailyLoss * daysToCount;
        productTotals[item.product_type] += historicalLoss;
      }
    });

    const totalAmount = Object.values(productTotals).reduce((sum, val) => sum + val, 0);

    return Object.entries(productTotals)
      .map(([product, value]) => ({
        name: product,
        value: Math.round(value),
        percentage: totalAmount > 0 ? ((value / totalAmount) * 100).toFixed(1) : '0'
      }))
      .sort((a, b) => b.value - a.value);
  }, [filteredItems, timeRange]);

  // 3. Meter Priority Matrix Data - Using actual historical data
  const priorityMatrixData = useMemo(() => {
    return filteredItems.map(item => {
      // Use actual historical loss/gain from backend if available
      let actualImpact = 0;
      if (item.historical_loss !== undefined) {
        actualImpact = item.historical_loss;
      } else if (item.historical_gain !== undefined) {
        actualImpact = item.historical_gain;
      } else {
        // Fallback to estimated impact
        const daysToCount = Math.min(item.days_out_of_compliance || 0, timeRange);
        actualImpact = Math.abs(item.daily_impact || 0) * daysToCount;
      }

      // Y-axis: positive for gains/exposures, negative for losses
      const dailyValue = Math.abs(item.daily_impact || 0);
      const yValue = item.impact_type === 'Gain' ? dailyValue : -dailyValue;

      // Use actual flow for bubble size (ZAxis will handle scaling)
      return {
        name: item.display_name,
        x: item.days_out_of_compliance,
        y: yValue,  // Positive for exposures, negative for losses
        z: item.average_annual_flow || 100000, // Use actual flow for bubble size
        product: item.product_type,
        impact: actualImpact, // Use actual historical impact
        impact_type: item.impact_type,
        meter_product_id: item.meter_product_id,
        actualFlow: item.average_annual_flow // Keep actual flow for tooltip
      };
    });
  }, [filteredItems, timeRange]);

  // Calculate symmetric Y-axis domain for Priority Matrix
  const yAxisDomain = useMemo(() => {
    if (priorityMatrixData.length === 0) return [-5000, 5000];

    const yValues = priorityMatrixData.map(d => Math.abs(d.y));
    const maxY = Math.max(...yValues);
    const roundedMax = Math.ceil(maxY / 1000) * 1000 + 1000;
    return [-roundedMax, roundedMax];
  }, [priorityMatrixData]);

  // 4. Top Loss Contributors - Based on actual historical losses from backend
  const topContributorsData = useMemo(() => {
    // Filter for losses and sort by impact
    const lossItems = filteredItems.filter(item => item.impact_type === 'Loss');

    // Calculate actual historical loss per meter
    const sorted = [...lossItems]
      .map((item, index) => {
        // Use display_name if available, otherwise construct from meter_id and product_type
        const displayName = item.display_name ||
                           (item.meter_id ? `${item.meter_id} - ${item.product_type || ''}` :
                            `Meter ${index + 1}`);

        // Use historical_loss from backend if available
        // This is the ACTUAL loss calculated from when the meter went in/out of compliance
        let historicalLoss = 0;

        if (item.historical_loss !== undefined) {
          // Backend has calculated actual historical loss for this meter
          historicalLoss = Math.abs(item.historical_loss || 0);
        } else {
          // Fallback: estimate based on days out and daily impact
          const daysToCount = Math.min(item.days_out_of_compliance || 0, timeRange);
          const dailyLoss = Math.abs(item.daily_impact || 0);
          historicalLoss = dailyLoss * daysToCount;
        }

        const result = {
          name: displayName,
          product: item.product_type || 'Unknown',
          loss: historicalLoss, // Use actual historical loss from backend
          drift: item.drift_percent || 0,
          displayName: displayName,  // Use same for both to ensure consistency
          daysOut: item.days_out_of_compliance || 0,
          dailyLoss: Math.abs(item.daily_impact || 0),
          meter_product_id: item.meter_product_id
        };

        return result;
      })
      .sort((a, b) => b.loss - a.loss) // Sort by actual historical loss
      .slice(0, 10); // Take top 10

    return sorted;
  }, [filteredItems, timeRange]);

  // 5. Cumulative Loss Data with Projection
  const cumulativeLossData = useMemo(() => {
    // For cumulative chart: show half historical, half projected
    // If timeRange is 30 days, show 15 days back, 15 days forward
    // If timeRange is 365 days, show 182 days back, 183 days forward
    const halfRange = Math.floor(timeRange / 2);
    const today = new Date();
    const startDate = subDays(today, halfRange);
    const endDate = addDays(today, halfRange);

    let cumulative = 0;
    const chartData: any[] = [];

    // HISTORICAL SECTION (past half of timeRange)
    // Use actual daily trend data for historical section
    const historicalData = dailyTrendData.slice(-halfRange); // Get last 'halfRange' days

    historicalData.forEach((day, index) => {
      cumulative += Math.abs(day.losses || 0);

      const dateFormat = timeRange <= 90 ? 'MMM dd' :
                        timeRange <= 365 ? 'MMM' :
                        'MMM yyyy';

      chartData.push({
        date: day.date,
        fullDate: day.fullDate || day.date,
        cumulative: cumulative,
        projection: null,  // No projection for historical data
        losses: day.losses,
        metersAffected: day.metersAffected || 0
      });
    });

    // Calculate average daily loss from actual historical data
    const avgDailyLoss = historicalData.length > 0
      ? historicalData.reduce((sum, day) => sum + Math.abs(day.losses || 0), 0) / historicalData.length
      : 0;

    // Get last actual cumulative value for projection starting point
    const lastCumulative = cumulative;

    // Add the connecting point (today's data connects historical to projection)
    if (chartData.length > 0) {
      chartData[chartData.length - 1] = {
        ...chartData[chartData.length - 1],
        projection: lastCumulative  // This creates the connection point
      };
    }

    // PROJECTION SECTION (future half of timeRange)
    let projectedCumulative = lastCumulative;

    for (let i = 1; i <= halfRange; i++) {
      const projDate = addDays(today, i);
      projectedCumulative += avgDailyLoss;

      const dateFormat = timeRange <= 90 ? 'MMM dd' :
                         timeRange <= 365 ? 'MMM' :
                         'MMM yyyy';

      chartData.push({
        date: format(projDate, dateFormat),
        fullDate: projDate.toISOString().split('T')[0],
        cumulative: null,  // No actual cumulative for projection
        projection: projectedCumulative,
        losses: null,
        metersAffected: 0
      });
    }

    // Cumulative chart calculated

    return chartData;
  }, [dailyTrendData, timeRange]);

  // Custom tooltip formatter
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Helper function to check if a chart is enabled
  const isChartEnabled = (chartId: string): boolean => {
    if (!config) return true; // Show all charts if no config provided (sidebar view)
    const chart = config.charts.find(c => c.id === chartId);
    return chart?.enabled !== false;
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>
              Time Range
            </Typography>
            <ToggleButtonGroup
              value={timeRange}
              exclusive
              onChange={(e, val) => val && setTimeRange(val)}
              size="small"
              sx={{ flexWrap: 'wrap' }}
            >
              <ToggleButton value={30}>Month</ToggleButton>
              <ToggleButton value={90}>Quarter</ToggleButton>
              <ToggleButton value={365}>Year</ToggleButton>
              <ToggleButton value={1095}>3 Years</ToggleButton>
              <ToggleButton value={1825}>5 Years</ToggleButton>
            </ToggleButtonGroup>
          </Grid>

          <Grid item xs={12} md={3}>
            <Typography variant="subtitle2" gutterBottom>
              Product Filter
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={selectedProduct}
                onChange={(e) => setSelectedProduct(e.target.value)}
              >
                {products.map(product => (
                  <MenuItem key={product} value={product}>
                    {product === 'ALL' ? 'All Products' : product}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="subtitle2" color="text.secondary">
                Total Daily Impact
              </Typography>
              <Typography variant="h4" color="error.main">
                {formatCurrency(Math.abs(summary?.total_daily_losses || 0))}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Charts Grid */}
      <Grid container spacing={3}>
        {/* 1. Daily Loss Trend */}
        {isChartEnabled('dailyTrend') && (
          <Grid item xs={12} lg={8}>
          <Card sx={{ position: 'relative' }}>
            <CalInsightButton
              componentType="daily-trend"
              data={{
                averageDailyLoss: Math.abs(summary?.total_daily_losses || 0),
                trendDirection: dailyTrendData.length > 1 ?
                  (dailyTrendData[dailyTrendData.length - 1].losses > dailyTrendData[0].losses ? 'increasing' : 'decreasing') :
                  'stable',
                lastSpike: dailyTrendData.reduce((max, day) =>
                  day.losses > max.losses ? day : max, dailyTrendData[0] || { losses: 0, date: '' }),
                timeRange,
                dataPoints: dailyTrendData
              }}
            />
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Daily Loss Trend
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={dailyTrendData}>
                  <defs>
                    <linearGradient id="lossGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={theme.palette.error.main} stopOpacity={0.8}/>
                      <stop offset="95%" stopColor={theme.palette.error.main} stopOpacity={0.1}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    interval={timeRange <= 90 ? Math.floor(dailyTrendData.length / 10) :
                             timeRange <= 365 ? Math.floor(dailyTrendData.length / 12) :
                             Math.floor(dailyTrendData.length / 15)}
                  />
                  <YAxis
                    tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                  />
                  <Tooltip
                    formatter={(value: any) => formatCurrency(Number(value) || 0)}
                    labelStyle={{ color: theme.palette.text.primary }}
                    contentStyle={{
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="losses"
                    stroke={theme.palette.error.main}
                    fill="url(#lossGradient)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        )}

        {/* 2. Product Distribution */}
        {isChartEnabled('productDistribution') && (
          <Grid item xs={12} lg={4}>
          <Card sx={{ position: 'relative' }}>
            <CalInsightButton
              componentType="product-distribution"
              data={{
                products: productDistributionData,
                topProduct: productDistributionData[0]?.name,
                topProductLoss: productDistributionData[0]?.value,
                meterCount: filteredItems.length,
                timeRange
              }}
            />
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Box>
                  <Typography variant="h6">
                    Loss by Product ({timeRange === 30 ? '30 Days' :
                                       timeRange === 90 ? 'Quarter' :
                                       timeRange === 365 ? 'Year' :
                                       timeRange === 1095 ? '3 Years' :
                                       timeRange === 1825 ? '5 Years' :
                                       `${timeRange} Days`})
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Based on current out-of-compliance meters
                  </Typography>
                </Box>
                <ToggleButtonGroup
                  value={pieDisplayMode}
                  exclusive
                  onChange={(e, val) => val && setPieDisplayMode(val)}
                  size="small"
                  sx={{ mr: 6 }}
                >
                  <ToggleButton value="percentage" sx={{ textTransform: 'none', px: 1, py: 0.5 }}>
                    %
                  </ToggleButton>
                  <ToggleButton value="value" sx={{ textTransform: 'none', px: 1, py: 0.5 }}>
                    $
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={productDistributionData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry: any) => {
                      // Only show label if segment is > 5% to prevent overlap
                      if (entry.percentage > 5) {
                        if (pieDisplayMode === 'percentage') {
                          return `${entry.name} (${entry.percentage}%)`;
                        } else {
                          const valueStr = entry.value >= 1000000
                            ? `$${(entry.value / 1000000).toFixed(1)}M`
                            : `$${(entry.value / 1000).toFixed(0)}k`;
                          return `${entry.name} ${valueStr}`;
                        }
                      }
                      return '';
                    }}
                    outerRadius={80}
                    fill="#0069AA"
                    dataKey="value"
                  >
                    {productDistributionData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={getProductColor(entry.name)}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value: any, name: any) => [
                      formatCurrency(Number(value) || 0),
                      name
                    ]}
                  />
                  <Legend
                    verticalAlign="bottom"
                    height={36}
                    formatter={(value, entry: any) => {
                      if (pieDisplayMode === 'percentage') {
                        return `${value} (${entry.payload.percentage}%)`;
                      } else {
                        const val = entry.payload.value;
                        const valueStr = val >= 1000000
                          ? `$${(val / 1000000).toFixed(1)}M`
                          : `$${(val / 1000).toFixed(0)}k`;
                        return `${value}: ${valueStr}`;
                      }
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        )}

        {/* 3. Top Loss Contributors */}
        {isChartEnabled('topContributors') && (
          <Grid item xs={12} lg={6}>
          <Card sx={{ position: 'relative' }}>
            <CalInsightButton
              componentType="top-10-loss"
              data={{
                meters: topContributorsData,
                totalLoss: topContributorsData.reduce((sum, item) => sum + item.loss, 0),
                timeRange,
                productTypes: Array.from(new Set(topContributorsData.map(item => item.product)))
              }}
            />
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top 10 Loss Contributors ({timeRange === 30 ? '30 Days' :
                                            timeRange === 90 ? 'Quarter' :
                                            timeRange === 365 ? 'Year' :
                                            timeRange === 1095 ? '3 Years' : '5 Years'})
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Showing top {Math.min(10, topContributorsData.length)} of {filteredItems.filter(i => i.impact_type === 'Loss').length} meters with historical losses
              </Typography>
              {topContributorsData.length > 0 ? (
                <Box sx={{ p: 2 }}>
                  {topContributorsData.map((item, index) => {
                    const maxLoss = Math.max(...topContributorsData.map(d => d.loss));
                    const barWidth = (item.loss / maxLoss) * 100;

                    return (
                      <Box
                        key={index}
                        sx={{
                          mb: 2.5,
                          cursor: onMeterClick ? 'pointer' : 'default',
                          '&:hover': onMeterClick ? { opacity: 0.8 } : {}
                        }}
                        onClick={() => onMeterClick?.(item.meter_product_id)}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                            {index + 1}. {item.displayName}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.error.main }}>
                            {formatCurrency(item.loss)}
                          </Typography>
                        </Box>
                        <Box sx={{ position: 'relative' }}>
                          <Box
                            sx={{
                              width: '100%',
                              height: 28,
                              bgcolor: 'grey.100',
                              borderRadius: 0.5,
                              overflow: 'hidden'
                            }}
                          >
                            <Box
                              sx={{
                                width: `${barWidth}%`,
                                height: '100%',
                                bgcolor: getProductColor(item.product),
                                transition: 'width 0.5s ease',
                                display: 'flex',
                                alignItems: 'center',
                                px: 1
                              }}
                            >
                              {barWidth > 20 && (
                                <Typography variant="caption" sx={{ color: 'white', fontWeight: 500 }}>
                                  {item.drift.toFixed(1)}% drift
                                </Typography>
                              )}
                            </Box>
                          </Box>
                          {barWidth <= 20 && (
                            <Typography
                              variant="caption"
                              sx={{
                                position: 'absolute',
                                left: `${barWidth}%`,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                ml: 1,
                                color: 'text.secondary'
                              }}
                            >
                              {item.drift.toFixed(1)}% drift
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })}

                  {/* Scale indicator */}
                  <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary">$0</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatCurrency(Math.max(...topContributorsData.map(d => d.loss)))}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ height: 350, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="text.secondary">No loss data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        )}

        {/* 4. Priority Matrix */}
        {isChartEnabled('priorityMatrix') && (
          <Grid item xs={12} lg={6}>
          <Card sx={{ position: 'relative' }}>
            <CalInsightButton
              componentType="financial-matrix"
              data={{
                highRiskCount: priorityMatrixData.filter(d => d.x > 30 && Math.abs(d.y) > 5000).length,
                totalExposure: priorityMatrixData.reduce((sum, d) => sum + Math.abs(d.impact), 0),
                quadrants: {
                  highVolumeHighDrift: priorityMatrixData.filter(d => d.x > 30 && Math.abs(d.y) > 5000).length,
                  highVolumeLowDrift: priorityMatrixData.filter(d => d.x <= 30 && Math.abs(d.y) > 5000).length,
                  lowVolumeHighDrift: priorityMatrixData.filter(d => d.x > 30 && Math.abs(d.y) <= 5000).length,
                  lowVolumeLowDrift: priorityMatrixData.filter(d => d.x <= 30 && Math.abs(d.y) <= 5000).length
                },
                timeRange
              }}
            />
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Meter Priority Matrix ({timeRange === 30 ? '30 Days' :
                                        timeRange === 90 ? 'Quarter' :
                                        timeRange === 365 ? 'Year' :
                                        timeRange === 1095 ? '3 Years' : '5 Years'})
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>High Priority:</strong> Far-right meters (long overdue) • <strong>Exposures:</strong> Above zero • <strong>Losses:</strong> Below zero
              </Typography>
              <ResponsiveContainer width="100%" height={350}>
                <ScatterChart margin={{ top: 10, right: 30, bottom: 60, left: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <ZAxis
                    type="number"
                    dataKey="z"
                    range={[50, 400]}
                    domain={[0, 'dataMax']}
                  />
                  <XAxis
                    dataKey="x"
                    name="Days Out"
                    type="number"
                    domain={[0, 'dataMax + 5']}
                    label={{
                      value: 'Days Since Last Proving (Overdue)',
                      position: 'insideBottom',
                      offset: -5,
                      style: { textAnchor: 'middle', fontSize: 12, fontWeight: 500 }
                    }}
                  />
                  <YAxis
                    dataKey="y"
                    name="Daily Impact"
                    domain={yAxisDomain}
                    tickFormatter={(value) => {
                      const absValue = Math.abs(value);
                      const kValue = absValue / 1000;
                      const formatted = kValue >= 1 ? `${kValue.toFixed(0)}k` : `${absValue.toFixed(0)}`;
                      return value < 0 ? `-$${formatted}` : `$${formatted}`;
                    }}
                    label={{
                      value: 'Daily Financial Impact ($)',
                      angle: -90,
                      position: 'insideLeft',
                      style: { textAnchor: 'middle', fontSize: 12, fontWeight: 500 }
                    }}
                  />
                  <ReferenceLine y={0} stroke="#666" strokeDasharray="3 3" />
                  <Tooltip
                    content={({ payload }) => {
                      if (!payload || payload.length === 0) return null;
                      const data = payload[0].payload;
                      const impactType = data.y >= 0 ? 'Exposure' : 'Loss';
                      return (
                        <Paper sx={{ p: 1 }}>
                          <Typography variant="subtitle2">{data.name}</Typography>
                          <Typography variant="body2">Product: {data.product}</Typography>
                          <Typography variant="body2">Days Overdue: {data.x}</Typography>
                          <Typography variant="body2">Daily {impactType}: {formatCurrency(Math.abs(data.y))}</Typography>
                          <Typography variant="body2">Annual Flow: {(data.actualFlow || 0).toLocaleString()} {unitLabels().volume}</Typography>
                          <Typography variant="body2">
                            {timeRange === 30 ? '30-Day' :
                             timeRange === 90 ? 'Quarter' :
                             timeRange === 365 ? 'Annual' :
                             timeRange === 1095 ? '3-Year' : '5-Year'} Impact: {formatCurrency(Math.abs(data.impact))}
                          </Typography>
                        </Paper>
                      );
                    }}
                  />
                  <Scatter
                    name="Meters"
                    data={priorityMatrixData}
                    fill={theme.palette.error.main}
                    cursor={onMeterClick ? 'pointer' : 'default'}
                    onClick={(data: any) => {
                      if (data && onMeterClick) {
                        onMeterClick(data.activePayload?.[0]?.payload?.meter_product_id || data.meter_product_id);
                      }
                    }}
                    onMouseEnter={(data: any) => {
                      if (data && data.activePayload) {
                        setHoveredMeterProduct(data.activePayload[0]?.payload?.meter_product_id);
                      }
                    }}
                    onMouseLeave={() => setHoveredMeterProduct(null)}
                  >
                    {priorityMatrixData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={getProductColor(entry.product)}
                        style={{
                          cursor: onMeterClick ? 'pointer' : 'default',
                          opacity: hoveredMeterProduct ? (hoveredMeterProduct === entry.meter_product_id ? 1 : 0.3) : 1,
                          strokeWidth: hoveredMeterProduct === entry.meter_product_id ? 2 : 0,
                          stroke: hoveredMeterProduct === entry.meter_product_id ? '#000' : 'none'
                        }}
                        onClick={() => onMeterClick?.(entry.meter_product_id)}
                      />
                    ))}
                  </Scatter>
                </ScatterChart>
              </ResponsiveContainer>

              {/* Compact Multi-Column Legend */}
              <Box sx={{
                mt: 1,
                pt: 1,
                borderTop: 1,
                borderColor: 'divider'
              }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontSize: '0.65rem' }}>
                  Meters by Product Type • Size = Flow • Click to view details
                </Typography>

                {/* Compact grid without scroll */}
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: 0.25,
                  fontSize: '0.6rem'
                }}>
                  {(() => {
                    // Group by product for better organization
                    const grouped = priorityMatrixData.reduce((acc, item) => {
                      if (!acc[item.product]) acc[item.product] = [];
                      acc[item.product].push(item);
                      return acc;
                    }, {} as Record<string, typeof priorityMatrixData>);

                    // Flatten grouped items with product headers
                    const items: (typeof priorityMatrixData[0] | { isHeader: true; product: string })[] = [];
                    Object.entries(grouped)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .forEach(([product, productItems]) => {
                        items.push({ isHeader: true, product } as any);
                        items.push(...productItems.sort((a, b) => a.name.localeCompare(b.name)));
                      });

                    return items.map((item, index) => {
                      if ('isHeader' in item && item.isHeader) {
                        return (
                          <Box
                            key={`header-${item.product}`}
                            sx={{
                              gridColumn: 'span 4',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              mt: index > 0 ? 0.5 : 0,
                              mb: 0.25,
                              fontWeight: 600
                            }}
                          >
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: getProductColor(item.product),
                                border: '1px solid rgba(0,0,0,0.3)'
                              }}
                            />
                            <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 600 }}>
                              {item.product}
                            </Typography>
                          </Box>
                        );
                      }

                      // Type guard to ensure it's not a header
                      const dataItem = item as typeof priorityMatrixData[0];
                      return (
                        <Box
                          key={dataItem.meter_product_id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.25,
                            cursor: onMeterClick ? 'pointer' : 'default',
                            padding: '1px 2px',
                            borderRadius: 0.5,
                            backgroundColor: hoveredMeterProduct === dataItem.meter_product_id ? 'action.selected' : 'transparent',
                            opacity: hoveredMeterProduct ? (hoveredMeterProduct === dataItem.meter_product_id ? 1 : 0.5) : 0.85,
                            transition: 'opacity 0.15s',
                            '&:hover': {
                              backgroundColor: 'action.hover',
                              opacity: 1
                            }
                          }}
                          onClick={() => onMeterClick?.(dataItem.meter_product_id)}
                          onMouseEnter={() => setHoveredMeterProduct(dataItem.meter_product_id)}
                          onMouseLeave={() => setHoveredMeterProduct(null)}
                          title={`${dataItem.name} • Flow: ${(dataItem.actualFlow || 0).toLocaleString()} ${unitLabels().volYear} • ${dataItem.impact_type === 'Gain' ? 'Exposure' : 'Loss'}: $${Math.abs(dataItem.y).toLocaleString()}/day`}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: '0.58rem',
                              lineHeight: 1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '100%'
                            }}
                          >
                            {dataItem.name.replace(` ${dataItem.product}`, '')}
                          </Typography>
                        </Box>
                      );
                    });
                  })()}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        )}

        {/* 5. Cumulative Loss Tracker */}
        {isChartEnabled('cumulativeLoss') && (
          <Grid item xs={12}>
          <Card sx={{ position: 'relative' }}>
            <CalInsightButton
              componentType="monthly-comparison"
              data={{
                currentMonth: cumulativeLossData.filter(d => d.cumulative !== null).pop()?.cumulative || 0,
                previousMonth: cumulativeLossData.filter(d => d.cumulative !== null)[0]?.cumulative || 0,
                changePercent: (() => {
                  const last = cumulativeLossData.filter(d => d.cumulative !== null).pop()?.cumulative || 0;
                  const first = cumulativeLossData.filter(d => d.cumulative !== null)[0]?.cumulative || 1;
                  return ((last - first) / first * 100).toFixed(1);
                })(),
                timeRange,
                trend: cumulativeLossData
              }}
            />
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Cumulative Loss Tracking
              </Typography>

              {/* Key Metrics Callouts */}
              {(() => {
                // Find the last actual (non-projection) data point for total loss
                const lastActualData = cumulativeLossData.filter(d => d.cumulative !== null).pop();
                const totalLoss = lastActualData?.cumulative || 0;
                const avgDailyLoss = totalLoss / (cumulativeLossData.filter(d => d.cumulative !== null).length || 1);

                // Calculate projection based on selected timeframe
                const getProjectionLabel = () => {
                  switch(timeRange) {
                    case 30: return 'Next Month';
                    case 90: return 'Next Quarter';
                    case 365: return 'Next Year';
                    case 1095: return 'Next 3 Years';
                    case 1825: return 'Next 5 Years';
                    default: return 'Next Period';
                  }
                };

                const projectedLoss = avgDailyLoss * timeRange;

                return (
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={4}>
                      <Paper sx={{ p: 1.5, bgcolor: 'background.default' }}>
                        <Typography variant="caption" color="text.secondary">
                          Total Loss ({timeRange} days)
                        </Typography>
                        <Typography variant="h6" color="error">
                          {formatCurrency(totalLoss)}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Paper sx={{ p: 1.5, bgcolor: 'background.default' }}>
                        <Typography variant="caption" color="text.secondary">
                          Average Daily Loss
                        </Typography>
                        <Typography variant="h6" color="error">
                          {formatCurrency(avgDailyLoss)}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Paper sx={{ p: 1.5, bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                        <Typography variant="caption">
                          Projected {getProjectionLabel()}
                        </Typography>
                        <Typography variant="h6">
                          {formatCurrency(projectedLoss)}
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                );
              })()}

              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={cumulativeLossData}>
                  <defs>
                    <linearGradient id="colorLoss" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={theme.palette.error.main} stopOpacity={0.8}/>
                      <stop offset="95%" stopColor={theme.palette.error.main} stopOpacity={0.1}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11 }}
                    interval={timeRange <= 90 ? Math.floor(cumulativeLossData.length / 10) :
                             timeRange <= 365 ? Math.floor(cumulativeLossData.length / 12) :
                             Math.floor(cumulativeLossData.length / 15)}
                  />
                  <YAxis
                    tickFormatter={(value) => {
                      if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
                      return `$${(value / 1000).toFixed(0)}k`;
                    }}
                  />
                  <Tooltip
                    formatter={(value: any, name: any) => {
                      if (name === 'Cumulative Loss') return [formatCurrency(Number(value) || 0), 'Total Loss'];
                      if (name === 'Daily Loss') return [formatCurrency(Number(value) || 0), 'Daily Loss'];
                      return [formatCurrency(Number(value) || 0), name];
                    }}
                    labelStyle={{ color: '#000' }}
                    contentStyle={{ backgroundColor: 'rgba(255,255,255,0.95)', border: '1px solid #ccc' }}
                  />
                  <Area
                    type="monotone"
                    dataKey="cumulative"
                    name="Cumulative Loss"
                    stroke={theme.palette.error.main}
                    strokeWidth={2}
                    fill="url(#colorLoss)"
                  />
                  <Line
                    type="monotone"
                    dataKey="projection"
                    name="Projected Loss"
                    stroke={theme.palette.info.main}
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={false}
                    connectNulls={true}
                  />
                  <Line
                    type="monotone"
                    dataKey="losses"
                    name="Daily Loss"
                    stroke={theme.palette.error.dark}
                    strokeWidth={1}
                    dot={false}
                    strokeOpacity={0.5}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default CostOfNonComplianceCharts;